<oaibox-stats-card
  [title]="order.title ?? 'Operation'"
  icon="fa-duotone fa-solid fa-bag-shopping"
  gradient="primary"
>
  <div class="content">
    <section id="bill-of-materials">
      <header>
        <div class="d-flex align-items-center g-g">
          <h2>Bill Of Materials</h2>

          <button
            id="download-bom-btn"
            type="button"
            (click)="downloadBillOfMaterials()"
            oaiboxBsPopover="Download data"
            placement="top"
            trigger="hover"
            container="body"
          >
            <i class="fa-solid fa-download"></i>
          </button>
        </div>

        <button
          id="add-item-btn"
          type="button"
          class="btn"
          [oaiboxBsPopover]="addItem"
          placement="right"
          trigger="click"
          container="body"
        >
          <i class="fa-solid fa-circle-plus"></i> Add Item
        </button>
      </header>

      <ul
        class="bill-of-materials-items-list"
        cdkDropList
        (cdkDropListDropped)="onBomDrop($event)"
      >
        @for (item of bom; track item.name) {
          <li class="bill-of-materials-item" cdkDrag>
            <i class="fa-solid fa-grip-lines" cdkDragHandle></i>

            <div class="d-flex align-items-baseline g-g ellipsis">
              <span class="ellipsis">{{
                item.quantity + "x " + item.name
              }}</span>
              <button
                type="button"
                class="edit-btn"
                (click)="changeSelectedBomItem(item)"
                [oaiboxBsPopover]="editItem"
                placement="right"
                trigger="click"
                container="body"
              >
                <i class="fa-solid fa-pen-to-square warning"></i>
              </button>
            </div>
          </li>
        }
      </ul>
    </section>

    <section id="order-info">
      <section>
        <div class="order-dates flex-fill">
          <div class="d-flex justify-content-between g-g">
            <div>
              <span>{{ order.startDate | date: "yyyy-MM-dd" }}</span>
              <div class="under-label">Start Date</div>
            </div>
            <div>
              <span [class.overdue-date]="dueDateIsOverdue">
                {{ order.dueDate | date: "yyyy-MM-dd" }}
              </span>
              <div class="under-label">Due Date</div>
            </div>
          </div>

          <div class="progress">
            <div
              class="bg-primary-lighter"
              role="progressbar"
              [class.bg-danger-lighter]="dueDateIsOverdue"
              [style.visibility]="
                order.orderType === OrderType.BRINGUP_DONE
                  ? 'hidden'
                  : 'visible'
              "
              [style.width.%]="orderProgress"
            ></div>
          </div>
        </div>
      </section>

      <div class="d-flex justify-content-between">
        <section>
          <h2>Customer</h2>

          <div class="d-flex flex-column">
            <span class="fw-bold">{{ order.customerName }}</span>
            <span>{{ order.customerEmailAddress }}</span>
            <div class="under-label">Email Address</div>
          </div>
        </section>

        <section id="on-prem-installation" class="fw-bold">
          <h2>
            {{
              order.onPremInstallation
                ? "On-Premises Installation"
                : "No On-Premises Installation"
            }}
          </h2>
        </section>
      </div>

      <section>
        <h2>Comments</h2>
        @if (order.comments) {
          <em class="fw-bold">{{ order.comments }}</em>
        } @else {
          --
        }
      </section>

      <section>
        <h2>Status</h2>

        @if (order.orderType) {
          <span class="fw-bold">{{
            ORDER_TYPE_CONFIG[order.orderType].label | uppercase
          }}</span>
        } @else {
          --
        }
      </section>

      <section id="assigned-tenants">
        <h2>Assigned Tenants</h2>

        @if (tenants.length > 0) {
          <div class="tenants">
            @for (tenant of tenants; track tenant.id) {
              <article class="tenant">
                <oaibox-tenant-info [tenant]="tenant"></oaibox-tenant-info>
              </article>
            }
          </div>
        } @else {
          --
        }
      </section>

      <section id="tracking-details">
        <h2>Tracking Details</h2>

        @if (order.trackingDetails) {
          <div class="d-flex flex-column g-g">
            <div class="d-flex justify-content-between g-g">
              <div>
                <span class="fw-bold">{{
                  order.trackingDetails.expeditionDate
                }}</span>
                <div class="under-label">Expedition Date</div>
              </div>

              <div>
                <span>{{ order.trackingDetails.waybill }}</span>
                <div class="under-label">Waybill</div>
              </div>
            </div>

            <div>
              <div class="d-flex align-items-baseline g-g">
                <span>{{ order.trackingDetails.trackingUrl }}</span>
                <a
                  id="link-btn"
                  [attr.href]="order.trackingDetails.trackingUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square"></i>
                </a>
              </div>
              <div class="under-label">Tracking URL</div>
            </div>
          </div>
        } @else {
          --
        }
      </section>
    </section>
  </div>
</oaibox-stats-card>

<ng-template #addItem>
  <form [formGroup]="createBomItemForm" (ngSubmit)="createBomItem()">
    <div class="d-flex g-g">
      <div>
        <label class="form-label">Name</label>
        <input
          type="text"
          class="form-control form-control-sm"
          [formControl]="createBomItemForm.controls.name"
        />
      </div>

      <div>
        <label class="form-label">Quantity</label>
        <input
          type="number"
          class="form-control form-control-sm"
          [formControl]="createBomItemForm.controls.quantity"
        />
      </div>
    </div>

    <footer>
      <button type="submit" class="btn btn-success">Save</button>
    </footer>
  </form>
</ng-template>

<ng-template #editItem>
  <form
    class="form"
    [formGroup]="updateBomItemForm"
    (ngSubmit)="updateBomItem()"
  >
    <div class="d-flex g-g">
      <div>
        <label class="form-label">Name</label>
        <input
          type="text"
          class="form-control form-control-sm"
          [formControl]="updateBomItemForm.controls.name"
        />
      </div>

      <div>
        <label class="form-label">Quantity</label>
        <input
          type="number"
          class="form-control form-control-sm"
          [formControl]="updateBomItemForm.controls.quantity"
        />
      </div>
    </div>

    <div class="stock-check-buttons">
      <button
        type="button"
        [class.not-available]="
          updateBomItemForm.controls.isAvailable.value === false
        "
        (click)="updateBomItemForm.controls.isAvailable.setValue(false)"
      >
        Not Available
      </button>
      <button
        type="button"
        [class.available]="
          updateBomItemForm.controls.isAvailable.value === true
        "
        (click)="updateBomItemForm.controls.isAvailable.setValue(true)"
      >
        Available
      </button>
    </div>

    <footer>
      <button type="submit" class="btn btn-success">Save</button>
    </footer>
  </form>
</ng-template>
